import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, ArrowRight, Check, MapPin, Users, Building2 } from "lucide-react";
import Seo, { localBusinessLd, buildFaqLd } from "../components/Seo";
import Reveal, { StaggerGroup, itemVariants } from "../components/Reveal";
import ServiceCard from "../components/ServiceCard";
import Marquee from "../components/Marquee";
import FaqAccordion from "../components/FaqAccordion";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import CtaSection from "../components/CtaSection";
import TestimonialCarousel from "../components/TestimonialCarousel";
import { api } from "../lib/api";
import { company, whatsappUrl, trustPoints, regionHubs } from "../data/site";
import { services } from "../data/services";
import { faqItems } from "../data/faq";
import vanImg from "../assets/rt-van.webp";
import teamImg from "../assets/rt-team-einsatz.webp";
import vanBrandedImg from "../assets/rt-van-branded.webp";

const VAN_ALT = "Rümpeltaxi Transporter gelb-schwarz, Entrümpelung Ulm Neu-Ulm Altenstadt";

const headlineLines = ["Wir schaffen", "Platz."];

const processSteps = [
  { n: "01", title: "Kontakt aufnehmen", text: "Sie schildern uns kurz Ihr Anliegen – telefonisch, per WhatsApp oder über das Anfrageformular." },
  { n: "02", title: "Objekt und Umfang klären", text: "Auf Grundlage Ihrer Angaben, Bilder oder einer Besichtigung verschaffen wir uns einen Überblick." },
  { n: "03", title: "Angebot erhalten", text: "Sie erhalten ein transparent aufgebautes Angebot für die vereinbarten Leistungen." },
  { n: "04", title: "Termin und Durchführung", text: "Nach der Beauftragung stimmen wir Ablauf und gewünschten Termin mit Ihnen ab." },
  { n: "05", title: "Übergabe", text: "Nach Abschluss werden die vereinbarten Arbeiten gemeinsam geprüft und das Objekt übergeben." },
];

const valuePoints = [
  "Persönliche Kommunikation",
  "Strukturierte Planung",
  "Diskreter Umgang mit sensiblen Situationen",
  "Sorgfältiger Umgang mit dem Objekt",
  "Abgestimmte Sortierung und Entsorgung",
  "Ergänzende Transport- und Montageleistungen",
  "Flexible Lösungen für kleine und große Projekte",
  "Regionale Einsatzbereitschaft",
];

function HeroHeadline() {
  return (
    <h1 className="font-heading text-5xl font-extrabold leading-[0.95] tracking-tighter text-white md:text-7xl lg:text-8xl">
      {headlineLines.map((line, i) => (
        <span key={i} className="mask-line">
          <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
          >
            {line === "Platz." ? (
              <span>
                Platz<span className="text-yellow">.</span>
              </span>
            ) : (
              line
            )}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  const [projects, setProjects] = useState([]);
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api
      .get("/projects/published")
      .then((r) => setProjects(r.data.filter((p) => p.show_home && p.before_image && p.after_image).slice(0, 3)))
      .catch(() => {});
    api
      .get("/reviews/published?home=true")
      .then((r) => setReviews(Array.isArray(r.data) ? r.data : []))
      .catch(() => setReviews([]));
  }, []);

  return (
    <div data-testid="page-home">
      <Seo
        path="/"
        title="Rümpeltaxi Altenstadt | Entrümpelung Ulm, Neu-Ulm & Region"
        description="Rümpeltaxi aus Altenstadt (Iller): Entrümpelung und Haushaltsauflösung in Ulm, Neu-Ulm, Illertissen und der Region – persönlich, diskret, zuverlässig. Tel. 0151 70004440."
        jsonLd={[localBusinessLd, buildFaqLd(faqItems)]}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] overflow-hidden bg-brand-black">
        <motion.div style={{ y: bgY }} className="absolute inset-0 -z-0">
          <img
            src={vanImg}
            alt="Rümpeltaxi-Transporter im Einsatz vor einem Wohngebäude"
            className="h-[120%] w-full object-cover object-center"
          />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-brand-black" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/50 to-brand-black/30" />

        <div className="container-page relative z-10 flex min-h-[92vh] flex-col justify-end pb-16 pt-32 md:pb-24">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="kicker text-yellow"
          >
            Entrümpelung · Haushaltsauflösung · Entsorgung
          </motion.span>

          <div className="mt-5">
            <HeroHeadline />
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="mt-6 max-w-2xl text-lg leading-relaxed text-light-gray/80 md:text-xl"
          >
            Entrümpelungen, Haushaltsauflösungen und Entsorgungen – persönlich, diskret und
            zuverlässig. Von der ersten Anfrage bis zur besenreinen Übergabe erhalten Sie eine
            strukturierte Abwicklung aus einer Hand.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.7 }}
            className="mt-3 max-w-2xl text-lg leading-relaxed text-light-gray/80 md:text-xl"
            data-testid="hero-region-line"
          >
            Im Raum Ulm, Neu-Ulm, Illertissen und Altenstadt.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.7 }}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap"
          >
            <Link
              to="/kontakt"
              data-testid="hero-cta-anfragen"
              className="inline-flex items-center justify-center gap-2 rounded-sm bg-yellow px-7 py-4 font-heading font-bold text-brand-black transition-colors hover:bg-yellow-dark"
            >
              Unverbindlich anfragen <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={company.phoneLink}
              data-testid="hero-cta-call"
              className="inline-flex items-center justify-center gap-2 rounded-sm border border-white/20 bg-white/5 px-7 py-4 font-heading font-bold text-white backdrop-blur-sm transition-colors hover:bg-white/10"
            >
              <Phone className="h-4 w-4" /> {company.phoneDisplay} anrufen
            </a>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 rounded-sm border border-white/20 bg-white/5 px-7 py-4 font-heading font-bold text-white backdrop-blur-sm transition-colors hover:bg-white/10"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
          >
            {trustPoints.map((t) => (
              <li key={t} className="flex items-center gap-2 text-sm font-semibold text-light-gray/80">
                <Check className="h-4 w-4 text-yellow" /> {t}
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      <Marquee />

      {/* UNSER TRANSPORTER */}
      <section className="bg-white py-20 md:py-28" data-testid="home-van">
        <div className="container-page grid items-center gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-16">
          <Reveal>
            <figure className="overflow-hidden rounded-md border-4 border-anthracite">
              <img
                src={vanBrandedImg}
                alt={VAN_ALT}
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
                data-testid="home-van-image"
              />
            </figure>
          </Reveal>
          <Reveal delay={1}>
            <span className="kicker">Unser Rümpeltaxi</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-anthracite md:text-4xl lg:text-5xl">
              Da wird sogar der Sperrmüll neidisch.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-mid-gray md:text-lg">
              Entrümpelung, Entsorgung, Transport, Abriss und Galabau – persönlich aus Altenstadt.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 rounded-sm bg-yellow px-6 py-3.5 font-heading font-bold text-brand-black transition-colors hover:bg-yellow-dark"
                data-testid="home-van-cta-kontakt"
              >
                Unverbindlich anfragen <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={company.phoneLink}
                className="inline-flex items-center justify-center gap-2 rounded-sm bg-brand-black px-6 py-3.5 font-heading font-bold text-white transition-colors hover:bg-dark-gray"
                data-testid="home-van-cta-call"
              >
                <Phone className="h-4 w-4" /> {company.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICE SELECTION */}
      <section className="bg-white py-20 md:py-28" data-testid="home-services">
        <div className="container-page">
          <Reveal className="max-w-2xl">
            <span className="kicker">Leistungen</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-anthracite md:text-4xl lg:text-5xl">
              Wobei dürfen wir Sie unterstützen?
            </h2>
          </Reveal>
          <StaggerGroup className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {services.slice(0, 8).map((s) => (
              <ServiceCard key={s.slug} service={s} />
            ))}
          </StaggerGroup>
          <Reveal className="mt-10">
            <Link
              to="/leistungen"
              className="inline-flex items-center gap-2 font-heading font-bold text-anthracite hover:text-yellow-dark"
              data-testid="home-all-services"
            >
              Alle Leistungen ansehen <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WARUM RÜMPELTAXI */}
      <section className="relative overflow-hidden bg-anthracite py-20 text-white md:py-28" data-testid="home-value">
        <div className="container-page grid gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <span className="kicker text-yellow">Warum Rümpeltaxi</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
              Professionelle Unterstützung, wenn Platz geschaffen werden muss.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-light-gray/70 md:text-lg">
              Eine Räumung bedeutet häufig mehr als das reine Entfernen von Möbeln und Gegenständen.
              Sie erfordert Planung, Rücksichtnahme und eine zuverlässige Durchführung. Rümpeltaxi
              begleitet Privatkunden, Angehörige, Eigentümer, Betreuer und Unternehmen von der ersten
              Abstimmung bis zur Übergabe des Objekts.
            </p>
          </Reveal>
          <StaggerGroup className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
            {valuePoints.map((v) => (
              <motion.div key={v} variants={itemVariants} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-yellow text-brand-black">
                  <Check className="h-4 w-4" />
                </span>
                <span className="text-sm text-light-gray/90 md:text-base">{v}</span>
              </motion.div>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* ABLAUF */}
      <section className="bg-light-gray py-20 md:py-28" data-testid="home-process">
        <div className="container-page">
          <Reveal className="max-w-2xl">
            <span className="kicker">Ablauf</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-anthracite md:text-4xl lg:text-5xl">
              So läuft Ihre Anfrage ab
            </h2>
          </Reveal>
          <div className="mt-12 space-y-px">
            {processSteps.map((step, i) => (
              <Reveal key={step.n} delay={i * 0.5}>
                <div className="group relative flex flex-col gap-3 border-t border-mid-gray/20 py-8 md:flex-row md:items-center md:gap-10">
                  <span className="font-heading text-5xl font-extrabold text-anthracite/10 transition-colors group-hover:text-yellow md:text-7xl md:w-40">
                    {step.n}
                  </span>
                  <h3 className="font-heading text-xl font-bold text-anthracite md:w-72 md:text-2xl">
                    {step.title}
                  </h3>
                  <p className="max-w-xl text-base leading-relaxed text-mid-gray">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RÜMPELTAXI IM EINSATZ */}
      <section className="bg-white py-20 md:py-28" data-testid="home-einsatz">
        <div className="container-page">
          <Reveal className="max-w-2xl">
            <span className="kicker">Rümpeltaxi im Einsatz</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-anthracite md:text-4xl lg:text-5xl">
              Unterwegs für unsere Kunden
            </h2>
            <p className="mt-5 text-base leading-relaxed text-mid-gray md:text-lg">
              Transport und Räumung aus einer Hand – professionell ausgestattet, für kleine und
              umfangreiche Projekte, regional und überregional im Einsatz.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-2">
            <Reveal>
              <figure className="relative overflow-hidden rounded-md border-4 border-anthracite">
                <img
                  src={teamImg}
                  alt="Rümpeltaxi-Mitarbeiter tragen ein Elektrogerät zum Transporter"
                  className="h-full max-h-[560px] w-full object-cover"
                  loading="lazy"
                />
                <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-brand-black/80 to-transparent p-5 text-sm font-semibold text-white">
                  Transport und Räumung aus einer Hand
                </figcaption>
              </figure>
            </Reveal>
            <Reveal delay={1}>
              <figure className="relative overflow-hidden rounded-md border-4 border-anthracite">
                <img
                  src={vanImg}
                  alt="Beschrifteter Rümpeltaxi-Transporter vor einem Wohngebäude"
                  className="h-full max-h-[560px] w-full object-cover"
                  loading="lazy"
                />
                <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-brand-black/80 to-transparent p-5 text-sm font-semibold text-white">
                  Professionell ausgestattet – regional und überregional
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VORHER-NACHHER (echte Projekte) */}
      <section className="bg-light-gray py-20 md:py-28" data-testid="home-beforeafter">
        <div className="container-page">
          <Reveal className="max-w-2xl">
            <span className="kicker">Ergebnisse</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-anthracite md:text-4xl lg:text-5xl">
              Ergebnisse, die man sieht
            </h2>
            <p className="mt-5 text-base leading-relaxed text-mid-gray md:text-lg">
              Jede Räumung ist anders. Unsere Vorher-Nachher-Einblicke zeigen, wie aus vollgestellten
              Räumen wieder nutzbare, übersichtliche und besenrein übergebene Flächen werden.
            </p>
          </Reveal>

          {projects.length === 0 ? (
            <Reveal className="mt-10">
              <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-mid-gray/30 bg-white px-6 py-16 text-center">
                <span className="rounded-sm bg-yellow px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-brand-black">In Vorbereitung</span>
                <p className="mt-5 max-w-md font-heading text-xl font-bold text-anthracite">Echte Vorher-Nachher-Projekte werden in Kürze ergänzt.</p>
              </div>
            </Reveal>
          ) : (
            <>
              <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {projects.map((p, i) => (
                  <Reveal key={p.id} delay={i * 0.5}>
                    <div data-testid={`home-project-${p.id}`}>
                      <BeforeAfterSlider before={p.before_image} after={p.after_image} alt={p.title} />
                      <h3 className="mt-4 font-heading text-lg font-bold text-anthracite">{p.title}</h3>
                      {p.object_type && <p className="text-sm text-mid-gray">{[p.object_type, p.service_type].filter(Boolean).join(" · ")}</p>}
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal className="mt-10">
                <Link to="/referenzen" className="inline-flex items-center gap-2 rounded-sm bg-brand-black px-6 py-3.5 font-heading font-bold text-white transition-colors hover:bg-dark-gray" data-testid="home-more-projects">
                  Weitere Vorher-Nachher-Projekte ansehen <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </>
          )}
        </div>
      </section>

      {/* PRIVAT / GESCHÄFT */}
      <section className="bg-light-gray py-20 md:py-28" data-testid="home-audiences">
        <div className="container-page grid gap-6 md:grid-cols-2">
          <Reveal>
            <div className="flex h-full flex-col rounded-sm border border-white bg-white p-8 md:p-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-yellow text-brand-black">
                <Users className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-heading text-2xl font-bold text-anthracite">
                Entlastung in einer oft herausfordernden Situation
              </h3>
              <p className="mt-4 flex-1 text-base leading-relaxed text-mid-gray">
                Ob Haushaltsauflösung, Nachlass, Umzug in eine kleinere Wohnung oder die Vorbereitung
                eines Immobilienverkaufs: Wir unterstützen Sie mit einer klaren und respektvollen
                Abwicklung.
              </p>
              <Link
                to="/privatkunden"
                className="mt-6 inline-flex items-center gap-2 font-heading font-bold text-anthracite hover:text-yellow-dark"
                data-testid="home-private-link"
              >
                Leistungen für Privatkunden <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={1}>
            <div className="flex h-full flex-col rounded-sm border border-dark-gray bg-brand-black p-8 text-white md:p-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-yellow text-brand-black">
                <Building2 className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-heading text-2xl font-bold">
                Planbare Lösungen für Unternehmen und Institutionen
              </h3>
              <p className="mt-4 flex-1 text-base leading-relaxed text-light-gray/70">
                Wir übernehmen auch umfangreiche Räumungs-, Transport- und Entsorgungsprojekte für
                Unternehmen, Hausverwaltungen, Kanzleien, gesetzliche Betreuer und soziale
                Einrichtungen.
              </p>
              <Link
                to="/geschaeftskunden"
                className="mt-6 inline-flex items-center gap-2 font-heading font-bold text-yellow hover:text-yellow-dark"
                data-testid="home-business-link"
              >
                Leistungen für Geschäftskunden <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EINSATZGEBIETE */}
      <section className="bg-white py-20 md:py-28" data-testid="home-regions">
        <div className="container-page">
          <Reveal className="max-w-2xl">
            <span className="kicker">Einsatzgebiete</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-anthracite md:text-4xl lg:text-5xl">
              Rümpeltaxi ist in Ihrer Region im Einsatz
            </h2>
          </Reveal>
          <StaggerGroup className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-2">
            {regionHubs.map((r) => (
              <motion.div key={r.slug} variants={itemVariants}>
                <Link
                  to={`/einsatzgebiete/${r.slug}`}
                  className="group flex items-center justify-between rounded-sm border border-light-gray bg-light-gray px-5 py-4 transition-colors hover:border-yellow hover:bg-white"
                  data-testid={`home-region-${r.slug}`}
                >
                  <span className="flex items-center gap-2 font-heading font-bold text-anthracite">
                    <MapPin className="h-4 w-4 text-yellow" /> {r.name}
                  </span>
                  <ArrowRight className="h-4 w-4 text-mid-gray transition-transform group-hover:translate-x-1" />
                </Link>
              </motion.div>
            ))}
          </StaggerGroup>
          <Reveal className="mt-8">
            <p className="text-base text-mid-gray">
              Ihr Ort ist nicht aufgeführt?{" "}
              <Link to="/kontakt" className="font-semibold text-anthracite underline hover:text-yellow-dark">
                Senden Sie uns Ihre Anfrage.
              </Link>{" "}
              Einsätze in weiteren Städten und Gemeinden prüfen wir gerne individuell.
            </p>
          </Reveal>
        </div>
      </section>

      {/* KUNDENSTIMMEN – rotierend, alle echten DB-Bewertungen */}
      {reviews.length > 0 && (
        <section className="bg-anthracite py-20 md:py-28" data-testid="home-testimonials">
          <div className="container-page">
            <Reveal className="max-w-2xl">
              <span className="kicker text-yellow">Kundenstimmen</span>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-white md:text-4xl lg:text-5xl">
                Das sagen unsere Kunden
              </h2>
            </Reveal>
            <TestimonialCarousel reviews={reviews} />
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="bg-white py-20 md:py-28" data-testid="home-faq">
        <div className="container-page grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
          <Reveal>
            <span className="kicker">FAQ</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-anthracite md:text-4xl lg:text-5xl">
              Häufige Fragen
            </h2>
            <p className="mt-6 text-base leading-relaxed text-mid-gray">
              Sie haben eine Frage, die hier nicht beantwortet wird?{" "}
              <Link to="/kontakt" className="font-semibold text-anthracite underline hover:text-yellow-dark">
                Kontaktieren Sie uns
              </Link>
              .
            </p>
          </Reveal>
          <Reveal delay={1}>
            <FaqAccordion items={faqItems} />
          </Reveal>
        </div>
      </section>

      <CtaSection />
    </div>
  );
}
